import { meQueryOptions } from "@/api/auth";
import { favouritesQuery, useMemberFeatures } from "@/api/me";
import { FavouriteCard } from "@/components/FavouriteCard";
import { ListingCardSkeleton } from "@/components/ListingCard";
import { ResourceCard } from "@/components/ResourceCard";
import { ButtonLink } from "@/components/ui/Link";
import { PageWrapper } from "@/components/ui/PageWrapper";
import { TopNavigation } from "@/components/ui/TopNavigation";
import { Box, Center, Flex, Grid, QueryHandler, Text } from "@givenwell/components";
import { HeartFilledIcon } from "@givenwell/core-solid-icons";
import { styled } from "@givenwell/stitches";
import { colors, fonts } from "@givenwell/theme";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useLayoutEffect, useRef, useState } from "react";
import { object, optional, picklist } from "valibot";

export const Route = createFileRoute("/_auth/_app/favourites")({
  component: Page,
  validateSearch: object({
    filter: optional(picklist(["listings", "resources"])),
  }),
});

function Page() {
  const query = useQuery(favouritesQuery);

  const [active, setActive] = useState<"marketplace" | "eap" | "resources">("marketplace");

  const scrollContainerRef = useRef<HTMLDivElement>(null!);

  useLayoutEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;
    const handleScroll = () => {
      const scrollY = scrollContainer.scrollTop;
      const marketplace = document.getElementById("marketplace");
      const eap = document.getElementById("eap");
      const resources = document.getElementById("resources");

      if (!marketplace || !resources || !eap) {
        return;
      }

      if (scrollY > 0 && scrollY >= scrollContainer.scrollHeight - scrollContainer.clientHeight) {
        setActive("resources");
      } else if (scrollY + scrollContainer.offsetTop + 50 > resources.offsetTop) {
        setActive("resources");
      } else if (scrollY + scrollContainer.offsetTop + 50 > eap.offsetTop) {
        setActive("eap");
      } else {
        setActive("marketplace");
      }
    };

    scrollContainer.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => {
      scrollContainer.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const marketplaceListings = query.data?.listings.filter(l => !l.eap_available) || [];
  const eapListings = query.data?.listings.filter(l => l.eap_available) || [];
  const resources = query.data?.resources || [];

  const empty =
    query.isSuccess && marketplaceListings.length === 0 && eapListings.length === 0 && resources.length === 0;

  const caps = useMemberFeatures();
  const me = useQuery(meQueryOptions);

  if (empty) {
    return (
      <PageWrapper>
        <Flex css={{ flexDirection: "column", flex: "1 0 0px", overflow: "hidden" }}>
          <TopNavigation title="Favourites" />
          <Flex css={{ flex: "1 0 0px", justify: "center", items: "center", flexDir: "column", align: "center" }}>
            <Flex
              css={{
                flexDirection: "column",
                width: "100%",
                maxW: 540,
                gap: 40,
                pl: "max(16px, var(--safe-area-inset-left))",
                pr: "max(16px, var(--safe-area-inset-right))",
                "@md": {
                  maxW: 500,
                },
              }}
            >
              <Box css={{ spaceY: 20 }}>
                <Center
                  css={{
                    backgroundColor: colors.warmGrey,
                    width: "100%",
                    w: 72,
                    h: 72,
                    mx: "auto",
                    borderRadius: "50%",
                  }}
                >
                  <HeartFilledIcon width="28" height="28" />
                </Center>
                <Box css={{ px: 20, width: "100%", maxW: 284, mx: "auto" }}>
                  <Text css={{ fontScale: "md", weight: 500 }}>No favourites</Text>
                </Box>
              </Box>
              <Flex css={{ flexDir: "column", gap: 16, "@md": { display: "grid", gridCols: 2 } }}>
                <ButtonLink variant="filled" size="lg" color="primary" to="/marketplace">
                  Go Shopping
                </ButtonLink>
                <ButtonLink
                  variant="tonal"
                  size="lg"
                  color="primary"
                  to="/nominate"
                  search={{
                    from: location.pathname + location.search,
                    email: me.data?.email,
                  }}
                  css={{ "@md": { order: -1 } }}
                >
                  Nominate a Supplier
                </ButtonLink>
              </Flex>
            </Flex>
          </Flex>
        </Flex>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <Flex css={{ flexDirection: "column", flex: "1 0 0px", overflow: "hidden" }}>
        <Box
          css={{
            width: "100%",
            maxW: 960 + 32,
            mx: "auto",
          }}
        >
          <TopNavigation title="Favourites" />
        </Box>
        <Flex css={{ minH: 44, borderBottom: `2px solid #F1F0F1`, px: 8, "@md": { display: "none" } }}>
          {caps.hasAllowanceMarketplace && (
            <MobileNavLink data-active={active === "marketplace" ? "" : undefined} href="#marketplace">
              Marketplace {query.isSuccess && <CountBadge>{marketplaceListings.length}</CountBadge>}
            </MobileNavLink>
          )}
          {caps.hasEap && (
            <MobileNavLink data-active={active === "eap" ? "" : undefined} href="#eap">
              Free Session Providers {query.isSuccess && <CountBadge>{eapListings.length}</CountBadge>}
            </MobileNavLink>
          )}
          <MobileNavLink data-active={active === "resources" ? "" : undefined} href="#resources">
            Resources {query.isSuccess && <CountBadge>{resources.length}</CountBadge>}
          </MobileNavLink>
        </Flex>
        <Flex css={{ flex: "1 0 0px", width: "100%", maxW: 960 + 32, mx: "auto", overflow: "hidden" }}>
          <Box
            css={{
              pr: 16,
              py: 16,
              borderRight: `1px solid rgba(0, 0, 0, 0.1)`,
              width: 320,
              display: "none",
              "@md": { display: "block" },
              "@lg": {
                pt: 0,
              },
            }}
          >
            {caps.hasAllowanceMarketplace && (
              <DesktopNavLink data-active={active === "marketplace" ? "" : undefined} href="#marketplace">
                Marketplace {query.isSuccess && <CountBadge>{marketplaceListings.length}</CountBadge>}
              </DesktopNavLink>
            )}
            {caps.hasEap && (
              <DesktopNavLink data-active={active === "eap" ? "" : undefined} href="#eap">
                Free Session Providers {query.isSuccess && <CountBadge>{eapListings.length}</CountBadge>}
              </DesktopNavLink>
            )}
            <DesktopNavLink data-active={active === "resources" ? "" : undefined} href="#resources">
              Resources {query.isSuccess && <CountBadge>{resources.length}</CountBadge>}
            </DesktopNavLink>
          </Box>
          <Box ref={scrollContainerRef} css={{ overflowY: "auto", flex: "1 0 0px", px: 16 }}>
            <QueryHandler
              query={query}
              success={({ data: { listings_metadata } }) => (
                <>
                  <Box
                    id="marketplace"
                    css={{
                      py: 16,
                      "@lg": {
                        pt: 0,
                      },
                    }}
                  >
                    {marketplaceListings.length > 0 && (
                      <Text css={{ weight: 500, pb: 16, fontFamily: fonts.ftHabit }}>Marketplace</Text>
                    )}
                    <Grid
                      css={{
                        gridTemplateColumns: "1fr 1fr",
                        gap: 16,
                      }}
                    >
                      {marketplaceListings.map(listing => (
                        <FavouriteCard
                          key={listing.listing_id}
                          listing={listing}
                          metadata={listings_metadata.find(md => md.listing_id == listing.listing_id)}
                        ></FavouriteCard>
                      ))}
                    </Grid>
                  </Box>
                  <Box
                    id="eap"
                    css={{
                      py: 16,
                      "@lg": {
                        pt: 0,
                      },
                    }}
                  >
                    {eapListings.length > 0 && (
                      <Text css={{ weight: 500, pb: 16, fontFamily: fonts.ftHabit }}>Free Session Providers</Text>
                    )}
                    <Grid
                      css={{
                        gridTemplateColumns: "1fr 1fr",
                        gap: 16,
                      }}
                    >
                      {eapListings.map(listing => (
                        <FavouriteCard
                          key={listing.listing_id}
                          listing={listing}
                          metadata={listings_metadata.find(md => md.listing_id == listing.listing_id)}
                        ></FavouriteCard>
                      ))}
                    </Grid>
                  </Box>
                  <Box
                    id="resources"
                    css={{
                      py: 16,
                      "@lg": {
                        pt: 0,
                      },
                    }}
                  >
                    {resources.length > 0 && (
                      <Text css={{ weight: 500, pb: 16, fontFamily: fonts.ftHabit }}>Resources</Text>
                    )}
                    <Grid
                      css={{
                        gridTemplateColumns: "1fr 1fr",
                        gap: 16,
                      }}
                    >
                      {resources.map(resource => (
                        <ResourceCard key={resource.resource_id} resource={resource} />
                      ))}
                    </Grid>
                  </Box>
                </>
              )}
              pending={() => (
                <Grid
                  css={{
                    gridTemplateColumns: "1fr 1fr",
                  }}
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(i => (
                    <ListingCardSkeleton key={i} />
                  ))}
                </Grid>
              )}
            />
          </Box>
        </Flex>
      </Flex>
    </PageWrapper>
  );
}

const MobileNavLink = styled("a", {
  display: "inline-flex",
  items: "center",
  height: "100%",
  px: 8,
  fontScale: "xs",
  color: colors.text2,
  "&[data-active]": {
    color: colors.navy,
    weight: 500,
    boxShadow: `0 2px 0 ${colors.navy}`,
  },
});

const DesktopNavLink = styled("a", {
  display: "flex",
  items: "center",
  height: 36,
  px: 16,
  rounded: 8,
  fontScale: "xs",
  color: colors.navy,
  justify: "space-between",
  weight: 500,
  "&[data-active]": {
    backgroundColor: colors.cyanLight,
  },
});

const CountBadge = styled("span", {
  display: "inline-flex",
  items: "center",
  justify: "center",
  height: 16,
  px: 4,
  borderRadius: 9999,
  bg: colors.warmGrey,
  color: "#78767C",
  fontScale: "xs",
  weight: 500,
  ml: 8,
  "[data-active] > &": {
    backgroundColor: colors.navy,
    color: "white",
  },
});
